// components/tools/text/FontFamilyControl.jsx
import React from 'react';
import { createPortal } from 'react-dom';
import { FONT_DEFAULT_FAMILY } from 'constants/fonts';

/**
 * FontFamilyControl
 * Props:
 * - value: current CSS font-family string
 * - onChange: (family: string) => void
 * - families?: Array<{ label: string, value: string }>
 * - portalRoot?: HTMLElement (defaults to document.body)
 *
 * Notes:
 * - The dropdown list is rendered into `portalRoot` via a React Portal so it won't be clipped
 *   by any `overflow: hidden/auto` ancestors.
 * - Ensure the families you present here are actually loaded (via Google Fonts, @font-face, etc.).
 */
export default function FontFamilyControl({
  value,
  onChange,
  families,
  portalRoot,
}) {
  // Default options if none provided
  const fallbackFamilies = [
    {
      label: 'Inter',
      value:
        'Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji"',
    },
    { label: 'Montserrat', value: 'Montserrat, Arial, sans-serif' },
    { label: 'Open Sans', value: '"Open Sans", Arial, sans-serif' },
    { label: 'Lato', value: 'Lato, Arial, sans-serif' },
    { label: 'Poppins', value: 'Poppins, Arial, sans-serif' },
    { label: 'Playfair Display', value: '"Playfair Display", Georgia, serif' },
    { label: 'Merriweather', value: 'Merriweather, Georgia, serif' },
    { label: 'Georgia', value: 'Georgia, serif' },
    { label: 'Times New Roman', value: '"Times New Roman", Times, serif' },
    { label: 'Courier New', value: '"Courier New", Courier, monospace' },
    { label: 'Fascinate Inline', value: '"Fascinate Inline", system-ui' },
    { label: 'Fuzzy Bubbles', value: '"Fuzzy Bubbles", sans-serif' },
    { label: 'Lobster', value: '"Lobster", sans-serif' },
    { label: 'Pacifico', value: '"Pacifico", cursive' },
    { label: 'Bebas Neue', value: '"Bebas Neue", sans-serif' },
    { label: 'Anton', value: '"Anton", sans-serif' },
    { label: 'Amatic SC', value: '"Amatic SC", sans-serif' },
    {
      label: 'System UI',
      value: 'system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial',
    },
  ];

  const options = families?.length ? families : fallbackFamilies;

  const currentValue = value || FONT_DEFAULT_FAMILY;
  const current = options.find((o) => o.value === currentValue) || {
    label: currentValue.split(',')[0],
    value: currentValue,
  };

  // UI state/refs
  const [open, setOpen] = React.useState(false);
  const [highlight, setHighlight] = React.useState(
    Math.max(
      0,
      options.findIndex((o) => o.value === current.value)
    )
  );
  const btnRef = React.useRef(null);
  const listRef = React.useRef(null);

  // Computed menu position (viewport coordinates, since we use position: fixed)
  const [menuPos, setMenuPos] = React.useState({
    top: 0,
    left: 0,
    width: 260,
    // When flipUp is true, menu will render above the button
    flipUp: false,
  });

  const positionMenu = React.useCallback(() => {
    const btn = btnRef.current;
    if (!btn) return;

    const r = btn.getBoundingClientRect();
    // Make menu a bit wider than the button for nicer labels
    const width = Math.max(220, Math.min(320, r.width + 80));
    const gap = 6;

    // Simple flip logic
    const estimatedHeight = 280; // px (max-height ~ 18rem); refined after render
    const spaceBelow = window.innerHeight - r.bottom;
    const spaceAbove = r.top;

    const flipUp =
      spaceBelow < Math.min(estimatedHeight + 16, window.innerHeight / 3) &&
      spaceAbove > spaceBelow;

    const top = flipUp ? Math.max(8, r.top - gap) : r.bottom + gap;
    const left = Math.min(Math.max(8, r.left), window.innerWidth - width - 8);

    setMenuPos({ top, left, width, flipUp });
  }, []);

  // Reposition when opening, scrolling, or resizing
  React.useEffect(() => {
    if (!open) return;
    positionMenu();

    const onScroll = () => positionMenu();
    const onResize = () => positionMenu();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open, positionMenu]);

  // Close on outside click
  React.useEffect(() => {
    const onDocDown = (e) => {
      if (!open) return;
      const inButton = btnRef.current?.contains(e.target);
      const inList = listRef.current?.contains(e.target);
      if (!inButton && !inList) setOpen(false);
    };
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, [open]);

  // After open, scroll the highlighted item into view
  React.useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector('[data-highlight="true"]');
    el?.scrollIntoView({ block: 'nearest' });
  }, [open]);

  // Event handlers
  const choose = (opt) => {
    onChange?.(opt.value);
    setOpen(false);
    // Restore focus to the button for accessibility
    btnRef.current?.focus();
  };

  const onButtonKeyDown = (e) => {
    const key = e.key;
    if (key === 'ArrowDown' || key === 'Enter' || key === ' ') {
      e.preventDefault();
      setOpen(true);
      // Ensure highlight tracks current selection
      const idx = Math.max(
        0,
        options.findIndex((o) => o.value === current.value)
      );
      setHighlight(idx);
      // Position immediately for keyboard open
      positionMenu();
    }
  };

  const onListKeyDown = (e) => {
    const key = e.key;
    if (key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      btnRef.current?.focus();
      return;
    }
    if (key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((i) => Math.min(i + 1, options.length - 1));
      return;
    }
    if (key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((i) => Math.max(i - 1, 0));
      return;
    }
    if (key === 'Home') {
      e.preventDefault();
      setHighlight(0);
      return;
    }
    if (key === 'End') {
      e.preventDefault();
      setHighlight(options.length - 1);
      return;
    }
    if (key === 'Enter') {
      e.preventDefault();
      choose(options[highlight]);
      return;
    }
  };

  // The actual menu (portalled)
  const Menu = (
    <div
      ref={listRef}
      role="listbox"
      tabIndex={-1}
      aria-label="Font family"
      onKeyDown={onListKeyDown}
      className={[
        'fixed z-[9999] max-h-72 overflow-auto rounded-md border border-gray-200 bg-white shadow-lg p-1',
        menuPos.flipUp ? 'translate-y-[-100%]' : '',
      ].join(' ')}
      style={{ top: menuPos.top, left: menuPos.left, width: menuPos.width }}
    >
      {options.map((opt, idx) => {
        const isActive = opt.value === current.value;
        const isHighlight = idx === highlight;
        return (
          <div
            key={opt.value}
            role="option"
            aria-selected={isActive}
            data-highlight={isHighlight ? 'true' : 'false'}
            onMouseEnter={() => setHighlight(idx)}
            onMouseDown={(e) => e.preventDefault()} // prevent focus loss before click
            onClick={() => choose(opt)}
            className={[
              'cursor-pointer px-3 py-2 rounded text-sm flex items-center justify-between',
              isHighlight ? 'bg-gray-100' : 'hover:bg-gray-50',
              isActive ? 'font-medium' : '',
            ].join(' ')}
            style={{ fontFamily: opt.value }}
            title={opt.label}
          >
            <span className="truncate">{opt.label}</span>
            <span className="ml-3 opacity-70">Aa</span>
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="relative inline-flex items-center gap-2">
      <span className="text-sm text-gray-600">Font</span>

      {/* Trigger button */}
      <button
        ref={btnRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => {
          const next = !open;
          setOpen(next);
          if (next) {
            const idx = Math.max(
              0,
              options.findIndex((o) => o.value === current.value)
            );
            setHighlight(idx);
            // Position right when opening via mouse
            positionMenu();
          }
        }}
        onKeyDown={onButtonKeyDown}
        className="h-9 min-w-[180px] px-3 rounded border border-gray-300 bg-white text-sm flex items-center justify-between gap-3"
        title="Font family"
      >
        <span className="truncate" style={{ fontFamily: current.value }}>
          {current.label}
        </span>
        <svg
          width="14"
          height="14"
          viewBox="0 0 20 20"
          aria-hidden
          className="opacity-70"
        >
          <path d="M5 7l5 6 5-6H5z" />
        </svg>
      </button>

      {/* Portal target: canvas overlay or document.body */}
      {open && createPortal(Menu, portalRoot ?? document.body)}
    </div>
  );
}
